import { useState } from "react";
import { useTranslation } from "react-i18next";
import swal from "sweetalert";
import { disable2FA, enable2FA, setup2FA } from "../../../../services/AuthService";

/**
 * Google Authenticator setup / disable UI for restaurant & franchise admins.
 */
export default function TwoFactorSettings({
  twoFactorEnabled,
  onEnabledChange,
}) {
  const { t } = useTranslation();
  const [qrCode, setQrCode] = useState("");
  const [manualSecret, setManualSecret] = useState("");
  const [setupCode, setSetupCode] = useState("");
  const [disableCode, setDisableCode] = useState("");
  const [disablePassword, setDisablePassword] = useState("");
  const [isSettingUp, setIsSettingUp] = useState(false);
  const [isEnabling, setIsEnabling] = useState(false);
  const [isDisabling, setIsDisabling] = useState(false);

  const handleSetup = async () => {
    try {
      setIsSettingUp(true);
      const data = await setup2FA();
      setQrCode(data.qrCode || "");
      setManualSecret(data.secret || "");
      setSetupCode("");
    } catch (err) {
      swal(
        "ERROR",
        err?.response?.data?.message ||
          t("twoFactorSetupError", {
            defaultValue: "Failed to start 2FA setup",
          }),
        "error"
      );
    } finally {
      setIsSettingUp(false);
    }
  };

  const handleEnable = async (event) => {
    event.preventDefault();
    if (!setupCode || setupCode.length < 6) {
      swal(
        "WARNING",
        t("authenticatorCodeRequired", {
          defaultValue: "Enter the 6-digit code from Google Authenticator",
        }),
        "warning"
      );
      return;
    }

    try {
      setIsEnabling(true);
      await enable2FA(setupCode.trim());
      onEnabledChange?.(true);
      setQrCode("");
      setManualSecret("");
      setSetupCode("");
      swal(
        "SUCCESS",
        t("twoFactorEnabledSuccess", {
          defaultValue:
            "Google Authenticator 2FA is now enabled. It will be required at login.",
        }),
        "success"
      );
    } catch (err) {
      swal(
        "ERROR",
        err?.response?.data?.message ||
          t("twoFactorEnableError", { defaultValue: "Failed to enable 2FA" }),
        "error"
      );
    } finally {
      setIsEnabling(false);
    }
  };

  const handleDisable = async (event) => {
    event.preventDefault();
    if (!disablePassword || !disableCode) {
      swal(
        "WARNING",
        t("twoFactorDisableRequired", {
          defaultValue:
            "Current password and authenticator code are required",
        }),
        "warning"
      );
      return;
    }

    try {
      setIsDisabling(true);
      await disable2FA(disableCode.trim(), disablePassword);
      onEnabledChange?.(false);
      setDisableCode("");
      setDisablePassword("");
      setQrCode("");
      setManualSecret("");
      swal(
        "SUCCESS",
        t("twoFactorDisabledSuccess", {
          defaultValue: "Two-factor authentication has been disabled.",
        }),
        "success"
      );
    } catch (err) {
      swal(
        "ERROR",
        err?.response?.data?.message ||
          t("twoFactorDisableError", {
            defaultValue: "Failed to disable 2FA",
          }),
        "error"
      );
    } finally {
      setIsDisabling(false);
    }
  };

  return (
    <div className="app-profile-account-panel app-profile-2fa pt-1">
      <div className="app-profile-account-panel__head">
        <div className="d-flex align-items-start justify-content-between flex-wrap gap-2">
          <div>
            <p className="app-profile-account-panel__eyebrow">
              {t("twoFactorAuthShort", { defaultValue: "2FA" })}
            </p>
            <h4 className="app-profile-account-panel__title">
              {t("twoFactorAuthTitle", {
                defaultValue: "Google Authenticator 2FA",
              })}
            </h4>
            <p className="app-profile-account-panel__desc mb-0">
              {t("twoFactorAuthHelp", {
                defaultValue:
                  "Protect your login with a one-time code from Google Authenticator.",
              })}
            </p>
          </div>
          <span
            className={`app-profile-2fa-badge ${
              twoFactorEnabled ? "is-on" : "is-off"
            }`}
          >
            {twoFactorEnabled
              ? t("enabled", { defaultValue: "Enabled" })
              : t("disabled", { defaultValue: "Disabled" })}
          </span>
        </div>
      </div>

      <div className="app-profile-account-panel__body settings-form">
        {!twoFactorEnabled ? (
          <div>
            {!qrCode ? (
              <div className="app-profile-2fa-empty">
                <p className="app-profile-2fa-empty__text">
                  {t("twoFactorSetupIntro", {
                    defaultValue:
                      "Scan a QR code with Google Authenticator to secure your account.",
                  })}
                </p>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSetup}
                  disabled={isSettingUp}
                >
                  {isSettingUp
                    ? t("generatingQr", { defaultValue: "Generating QR..." })
                    : t("setupGoogleAuthenticator", {
                        defaultValue: "Setup Google Authenticator",
                      })}
                </button>
              </div>
            ) : (
              <form onSubmit={handleEnable} className="app-profile-2fa-setup">
                <div className="app-profile-2fa-qr">
                  <img
                    src={qrCode}
                    alt="Google Authenticator QR code"
                    className="app-profile-2fa-qr__img"
                  />
                </div>
                {manualSecret ? (
                  <div className="form-group mb-3">
                    <label className="form-label">
                      {t("manualKey", {
                        defaultValue: "Manual key (if you cannot scan)",
                      })}
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={manualSecret}
                      readOnly
                      onFocus={(e) => e.target.select()}
                    />
                  </div>
                ) : null}
                <div className="form-group mb-3">
                  <label className="form-label">
                    {t("authenticatorCodeTitle", {
                      defaultValue: "Authenticator code",
                    })}
                  </label>
                  <input
                    type="text"
                    className="form-control app-profile-2fa-code"
                    placeholder="000000"
                    value={setupCode}
                    onChange={(e) =>
                      setSetupCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                    }
                    inputMode="numeric"
                    autoComplete="one-time-code"
                  />
                </div>
                <div className="d-flex gap-2 flex-wrap">
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isEnabling}
                  >
                    {isEnabling
                      ? t("enabling", { defaultValue: "Enabling..." })
                      : t("enable2FA", { defaultValue: "Enable 2FA" })}
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary app-profile-btn-ghost"
                    onClick={handleSetup}
                    disabled={isSettingUp}
                  >
                    {t("regenerateQr", { defaultValue: "Regenerate QR" })}
                  </button>
                </div>
              </form>
            )}
          </div>
        ) : (
          <form onSubmit={handleDisable} className="app-profile-2fa-disable">
            <p className="app-profile-account-panel__desc">
              {t("twoFactorDisableHelp", {
                defaultValue:
                  "2FA is active. Enter your password and a current authenticator code to disable it.",
              })}
            </p>
            <div className="form-group mb-3">
              <label className="form-label">{t("oldPwd")}</label>
              <input
                type="password"
                className="form-control"
                value={disablePassword}
                onChange={(e) => setDisablePassword(e.target.value)}
                autoComplete="current-password"
              />
            </div>
            <div className="form-group mb-3">
              <label className="form-label">
                {t("authenticatorCodeTitle", {
                  defaultValue: "Authenticator code",
                })}
              </label>
              <input
                type="text"
                className="form-control app-profile-2fa-code"
                placeholder="000000"
                value={disableCode}
                onChange={(e) =>
                  setDisableCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                }
                inputMode="numeric"
                autoComplete="one-time-code"
              />
            </div>
            <button
              type="submit"
              className="btn btn-outline-danger app-profile-btn-danger"
              disabled={isDisabling}
            >
              {isDisabling
                ? t("disabling", { defaultValue: "Disabling..." })
                : t("disable2FA", { defaultValue: "Disable 2FA" })}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
